import { Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';

import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../shared/components/UI/Button/Button';
import { TextArea } from '../../../../../../shared/components/UI/TextArea/TextArea';
import { TextInput } from '../../../../../../shared/components/UI/TextInput/TextInput';

import IconWandAi from '../../../../../../shared/assets/ai_icons/wand.svg?react';
import { VaultPropertyDeleteSection } from './components/VaultPropertyDeleteSection';
import { VaultPropertyFormLayout } from './components/VaultPropertyFormLayout';
import { VaultPropertyOptionInputs } from './components/VaultPropertyOptionInputs';
import { VaultPropertyTypeSelect } from './components/VaultPropertyTypeSelect';
import { useVaultPropertyForm } from './hooks/useVaultPropertyForm';

export function UpdatePropertySidePanelHeader() {
  const { t } = useTranslation('bidResponse');
  const theme = useMantineTheme();

  return (
    <Group spacing="01" maw="660px">
      <IconWandAi />
      <Title
        order={4}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing['01'],
        }}
      >
        {t('vault.sidepanels.updateProperty.title')}
      </Title>
    </Group>
  );
}

export function UpdateVaultPropertySidePanel({
  vaultPropertyId,
  onClose,
}: {
  vaultPropertyId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation(['bidResponse', 'common']);
  const { form, updateVaultProperty, isSubmitting, ...optionHandlers } =
    useVaultPropertyForm(vaultPropertyId);

  const handleSubmit = ({
    withTagsInference,
  }: {
    withTagsInference?: boolean;
  }) => {
    updateVaultProperty(withTagsInference)();
    onClose();
  };

  return (
    <Stack h="calc(100% - 65px)" maw="660px" px="05" spacing="05">
      <Text variant="sm" fw="400" c="gray.6" sx={{ whiteSpace: 'pre-line' }}>
        {t('vault.sidepanels.createProperty.subtitle')}
      </Text>
      <VaultPropertyFormLayout>
        <TextInput
          {...form.getInputProps('name')}
          label={t('vault.sidepanels.createProperty.input.name.label')}
          placeholder={t(
            'vault.sidepanels.createProperty.input.name.placeholder',
          )}
          variant="filled"
          disabled={isSubmitting}
        />
        <VaultPropertyTypeSelect form={form} disabled />
        <TextArea
          {...form.getInputProps('description')}
          variant="filled"
          minRows={4}
          label={t('vault.sidepanels.createProperty.input.description.label')}
          placeholder={t(
            'vault.sidepanels.createProperty.input.description.placeholder',
          )}
          disabled={isSubmitting}
        />
        <VaultPropertyOptionInputs
          form={form}
          isSubmitting={isSubmitting}
          {...optionHandlers}
        />
        <VaultPropertyDeleteSection
          vaultPropertyId={vaultPropertyId}
          onDelete={onClose}
        />
      </VaultPropertyFormLayout>
      <UpdatePropertySidePanelFooter
        onClose={onClose}
        isSubmitDisabled={!form.isValid() || isSubmitting}
        handleSubmit={handleSubmit}
      />
    </Stack>
  );
}

type UpdatePropertySidePanelFooterProps = {
  onClose: () => void;
  isSubmitDisabled: boolean;
  handleSubmit: ({
    withTagsInference,
  }: {
    withTagsInference?: boolean;
  }) => void;
};

function UpdatePropertySidePanelFooter({
  onClose,
  isSubmitDisabled,
  handleSubmit,
}: UpdatePropertySidePanelFooterProps) {
  const { t } = useTranslation(['bidResponse', 'common']);

  return (
    <Group noWrap position="right" spacing="03" pt="03" pr="04">
      <Button variant="white" onClick={onClose}>
        {t('common:cancel')}
      </Button>
      <Button
        variant="subtle"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
      >
        {t('common:save')}
      </Button>
      <Button
        onClick={() => handleSubmit({ withTagsInference: true })}
        disabled={isSubmitDisabled}
      >
        {t('vault.sidepanels.updateProperty.updateAndTrigger')}
      </Button>
    </Group>
  );
}
